import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createWorkgroup, saveWorkgroupTypes, saveOnboardingState, loadOnboardingState, createExamplePrompts, clearOnboardingState } from './firestoreFunctions';
import IntegrationSelectionList from './IntegrationSelectionList';
import { listIntegrationSummaries } from '../api/integrations';
import SettingsPage from './SettingsPage';
const OnboardingFlow = ({ user }) => {
  const [step, setStep] = useState(1);
  const [workgroupName, setWorkgroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [createdWorkgroupName, setCreatedWorkgroupName] = useState(null);
  const [currentConfigStep, setCurrentConfigStep] = useState(0);
  const [isLoadingState, setIsLoadingState] = useState(true);
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [integrationError, setIntegrationError] = useState(null);
  const [pendingIntegrationId, setPendingIntegrationId] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return 0;
    }
    return window.innerHeight || 0;
  });

  // Add state for system configurations
  const [salesforceConfig, setSalesforceConfig] = useState({
    username: '',
    password: '',
    security_token: '',
    url: ''
  });
  const [awsConfig, setAWSConfig] = useState({
    aws_access_key_id: '',
    aws_secret_access_key: '',
    region_name: 'us-east-1'
  });
  const [wooCommerceConfig, setWooCommerceConfig] = useState({
    url: '',
    consumer_key: '',
    consumer_secret: ''
  });
  const [maximoConfig, setMaximoConfig] = useState({
    api_key: '',
    url: ''
  });
  const [integrationSettings, setIntegrationSettings] = useState({});

  const [saveSettings, setSaveSettings] = useState(false);
  const [pendingConfigAction, setPendingConfigAction] = useState(null);
  const integrationDisplayNames = useMemo(() => {
    const mapping = {};
    availableIntegrations.forEach((integration) => {
      if (!integration || typeof integration.id !== 'string') {
        return;
      }
      const { id, name } = integration;
      if (typeof name === 'string' && name.trim() !== '') {
        mapping[id] = name;
      } else {
        mapping[id] = id.charAt(0).toUpperCase() + id.slice(1);
      }
    });
    return mapping;
  }, [availableIntegrations]);

  const integrationOrder = useMemo(
    () => availableIntegrations.map((integration) => integration.id),
    [availableIntegrations]
  );

  const getIntegrationDisplayName = (type) => {
    if (!type) return '';
    return integrationDisplayNames[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Calculate total steps (2 base steps + number of selected systems)
  const totalSteps = 2 + selectedTypes.length;

  // Get current system being configured
  const currentSystem = selectedTypes[currentConfigStep];

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleResize = () => setViewportHeight(window.innerHeight || 0);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const shell = document.querySelector('.main-shell');
    const previousShellBg = shell ? shell.style.backgroundColor : null;
    const previousBodyBg = document.body ? document.body.style.backgroundColor : null;
    const previousRootBg = document.documentElement
      ? document.documentElement.style.backgroundColor
      : null;
    const isDarkMode = document.documentElement?.classList?.contains('dark');
    const backgroundColor = isDarkMode ? '#0f172a' : '#ffffff';

    if (shell) {
      shell.style.backgroundColor = backgroundColor;
    }
    if (document.body) {
      document.body.style.backgroundColor = backgroundColor;
    }
    if (document.documentElement) {
      document.documentElement.style.backgroundColor = backgroundColor;
    }

    return () => {
      if (shell) {
        shell.style.backgroundColor = previousShellBg || '';
      }
      if (document.body) {
        document.body.style.backgroundColor = previousBodyBg || '';
      }
      if (document.documentElement) {
        document.documentElement.style.backgroundColor = previousRootBg || '';
      }
    };
  }, []);

  const isShortScreen = viewportHeight > 0 && viewportHeight < 720;
  const topPaddingOffset = isShortScreen ? '0.75rem' : '2.5rem';
  const bottomPaddingOffset = isShortScreen ? '2rem' : '4rem';
  const stageScrollOffset = isShortScreen ? '0.75rem' : '1.25rem';

  const onboardingContainerStyle = useMemo(
    () => ({
      paddingTop: `calc(var(--topnav-height, 56px) + ${topPaddingOffset})`,
      paddingBottom: `calc(${bottomPaddingOffset} + env(safe-area-inset-bottom, 0px))`,
      minHeight: '100%',
      width: '100%',
      flex: '1 0 auto'
    }),
    [bottomPaddingOffset, topPaddingOffset]
  );
  const stageAnchorStyle = useMemo(
    () => ({
      scrollMarginTop: `calc(var(--topnav-height, 56px) + ${stageScrollOffset})`
    }),
    [stageScrollOffset]
  );

  const progressContainerRef = useRef(null);
  const stepRefs = useRef({});

  // Add effect to scroll current step into view
  useEffect(() => {
    const scrollToCurrentStep = () => {
      const container = progressContainerRef.current;
      if (!container) return;

      // Calculate current step number
      const currentStepNumber = step === 3 ? 3 + currentConfigStep : step;
      const currentStepRef = stepRefs.current[currentStepNumber];
      if (!currentStepRef) return;

      // Get container and step measurements
      const containerRect = container.getBoundingClientRect();
      const stepRect = currentStepRef.getBoundingClientRect();

      // Calculate the center position
      const containerCenter = containerRect.left + (containerRect.width / 2);
      const stepCenter = stepRect.left + (stepRect.width / 2);

      // Calculate how far we need to scroll
      const scrollAmount = stepCenter - containerCenter;

      // Scroll the container
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    };

    // Small delay to ensure DOM is updated
    setTimeout(scrollToCurrentStep, 100);
  }, [step, currentConfigStep]);

  useEffect(() => {
    let cancelled = false;

    const fetchIntegrations = async () => {
      setIntegrationsLoading(true);
      try {
        const workgroupId = createdWorkgroupName && createdWorkgroupName.trim() !== '' ? createdWorkgroupName : undefined;
        const integrations = await listIntegrationSummaries(workgroupId);
        if (!cancelled) {
          const list = Array.isArray(integrations) ? integrations : [];
          setAvailableIntegrations(list);
          setIntegrationError(null);
        }
      } catch (err) {
        console.error("Error fetching integrations:", err);
        if (!cancelled) {
          setAvailableIntegrations([]);
          setIntegrationError('Failed to load integrations. Please try again later.');
        }
      } finally {
        if (!cancelled) {
          setIntegrationsLoading(false);
        }
      }
    };

    fetchIntegrations();

    return () => {
      cancelled = true;
    };
  }, [createdWorkgroupName]);

  // Load saved onboarding state when component mounts
  useEffect(() => {
    const loadSavedState = async () => {
      if (!user?.uid) return;

      try {
        const savedState = await loadOnboardingState(user.uid);
        if (savedState) {
          setStep(savedState.step || 1);
          setWorkgroupName(savedState.workgroupName || '');
          setSelectedTypes(savedState.selectedTypes || []);
          setCreatedWorkgroupName(savedState.createdWorkgroupName || null);
          setCurrentConfigStep(savedState.currentConfigStep || 0);

          // Restore system configurations
          if (savedState.salesforceConfig) setSalesforceConfig(savedState.salesforceConfig);
          if (savedState.awsConfig) setAWSConfig(savedState.awsConfig);
          if (savedState.wooCommerceConfig) setWooCommerceConfig(savedState.wooCommerceConfig);
          if (savedState.maximoConfig) setMaximoConfig(savedState.maximoConfig);
          if (savedState.integrationSettings && typeof savedState.integrationSettings === 'object') {
            setIntegrationSettings(savedState.integrationSettings);
          } else {
            const legacySettings = {};
            if (savedState.salesforceConfig) legacySettings.salesforce = savedState.salesforceConfig;
            if (savedState.awsConfig) legacySettings.aws = savedState.awsConfig;
            if (savedState.wooCommerceConfig) legacySettings.woocommerce = savedState.wooCommerceConfig;
            if (savedState.maximoConfig) legacySettings.maximo = savedState.maximoConfig;
            if (Object.keys(legacySettings).length > 0) {
              setIntegrationSettings(legacySettings);
            }
          }
        }
      } catch (error) {
        console.error("Error loading onboarding state:", error);
        setError("Failed to load saved progress. Starting fresh.");
      } finally {
        setIsLoadingState(false);
      }
    };

    loadSavedState();
  }, [user]);

  useEffect(() => {
    if (!availableIntegrations || availableIntegrations.length === 0) {
      return;
    }

    setSelectedTypes((prevTypes) => {
      if (!Array.isArray(prevTypes) || prevTypes.length === 0) {
        return prevTypes;
      }
      const validIds = new Set(availableIntegrations.map((integration) => integration.id));
      const filtered = prevTypes.filter((id) => validIds.has(id));
      if (filtered.length === 0) {
        return filtered;
      }
      const ordered = availableIntegrations
        .map((integration) => integration.id)
        .filter((id) => filtered.includes(id));
      const leftovers = filtered.filter((id) => !ordered.includes(id));
      const combined = [...ordered, ...leftovers];
      if (
        combined.length === prevTypes.length &&
        combined.every((id, index) => id === prevTypes[index])
      ) {
        return prevTypes;
      }
      return combined;
    });
  }, [availableIntegrations]);

  // Save onboarding state whenever relevant state changes
  useEffect(() => {
    const saveState = async () => {
      if (!user?.uid || isLoadingState) return;

      try {
        await saveOnboardingState(user.uid, {
          step,
          workgroupName,
          selectedTypes,
          createdWorkgroupName,
          currentConfigStep,
          salesforceConfig,
          awsConfig,
          wooCommerceConfig,
          maximoConfig,
          integrationSettings
        });
      } catch (error) {
        console.error("Error saving onboarding state:", error);
      }
    };

    saveState();
  }, [
    user,
    step,
    workgroupName,
    selectedTypes,
    createdWorkgroupName,
    currentConfigStep,
    salesforceConfig,
    awsConfig,
    wooCommerceConfig,
    maximoConfig,
    integrationSettings,
    isLoadingState
  ]);

  // Clear onboarding state when user completes onboarding
  const clearSavedOnboardingState = async () => {
    if (!user?.uid) {
      console.log('No user ID available for clearing onboarding state');
      return;
    }

    try {
      await clearOnboardingState(user.uid);
      console.log('Successfully cleared onboarding state');
    } catch (error) {
      console.error("Error clearing onboarding state:", error);
    }
  };

  const handleCreateWorkgroup = async () => {
    if (!workgroupName) {
      setError("Workgroup name cannot be empty");
      return;
    }

    if (!/^[a-zA-Z0-9 ]+$/.test(workgroupName)) {
      setError("Group names can only contain numbers, letters and spaces");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createWorkgroup(workgroupName);
      // If we get here, the workgroup was created successfully
      setCreatedWorkgroupName(workgroupName);
      
      // Save the selected integrations to the newly created workgroup
      if (selectedTypes.length > 0) {
        await saveWorkgroupTypes(workgroupName, selectedTypes);
      }
      
      setStep(3);
    } catch (err) {
      // Extract error message from the error object
      const errorMessage = err.response?.data?.message || err.message || "Failed to create workgroup";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeToggle = async (type, shouldSelect) => {
    try {
      setPendingIntegrationId(type);
      setLoading(true);
      setIntegrationError(null);
      setError(null);

      const updatedSelection = new Set(selectedTypes);
      if (shouldSelect) {
        updatedSelection.add(type);
      } else {
        updatedSelection.delete(type);
      }

      let orderedTypes = [];
      if (integrationOrder && integrationOrder.length > 0) {
        orderedTypes = integrationOrder.filter((id) => updatedSelection.has(id));
        updatedSelection.forEach((id) => {
          if (!orderedTypes.includes(id)) {
            orderedTypes.push(id);
          }
        });
      } else {
        orderedTypes = Array.from(updatedSelection);
      }

      // Save to user's onboarding state if we don't have a workgroup name yet
      if (!createdWorkgroupName && user?.uid) {
        await saveOnboardingState(user.uid, {
          step,
          workgroupName,
          selectedTypes: orderedTypes,
          createdWorkgroupName,
          currentConfigStep,
          salesforceConfig,
          awsConfig,
          wooCommerceConfig,
          maximoConfig,
          integrationSettings
        });
      }
      // Save to Firestore if we have a workgroup name
      else if (createdWorkgroupName) {
        await saveWorkgroupTypes(createdWorkgroupName, orderedTypes);
      }

      setSelectedTypes(orderedTypes);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update workgroup types";
      setError(errorMessage);
    } finally {
      setLoading(false);
      setPendingIntegrationId(null);
    }
  };

  const handleConfigSaveSuccess = async (type, values) => {
    const normalizedType = type || currentSystem;
    if (normalizedType) {
      setIntegrationSettings((prev) => ({
        ...prev,
        [normalizedType]: { ...(values || {}) }
      }));
      if (normalizedType === 'salesforce' || normalizedType === 'sf') {
        setSalesforceConfig(values || {});
      } else if (normalizedType === 'aws') {
        setAWSConfig(values || {});
      } else if (normalizedType === 'woocommerce') {
        setWooCommerceConfig(values || {});
      } else if (normalizedType === 'maximo' || normalizedType === 'maximo_sql') {
        setMaximoConfig(values || {});
      }
    }

    setSaveSettings(false);

    if (!pendingConfigAction) {
      setLoading(false);
      return;
    }

    if (pendingConfigAction === 'next') {
      setCurrentConfigStep((prev) => prev + 1);
      setStep(3);
      setLoading(false);
      setPendingConfigAction(null);
      return;
    }

    if (pendingConfigAction === 'finish') {
      try {
        const response = await saveWorkgroupTypes(createdWorkgroupName, selectedTypes);
        if (response?.error) {
          throw new Error(response.error);
        }

        await createExamplePrompts(createdWorkgroupName, selectedTypes);
        await clearSavedOnboardingState();
        await new Promise(resolve => setTimeout(resolve, 1000));
        window.location.href = '/';
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Failed to save configuration";
        setError(errorMessage);
        setLoading(false);
      } finally {
        setPendingConfigAction(null);
      }
    }
  };

  const handleConfigSaveError = (message) => {
    setError(message || 'Failed to save configuration');
    setSaveSettings(false);
    setPendingConfigAction(null);
    setLoading(false);
  };

  const handleNextConfig = () => {
    setLoading(true);
    setError(null);
    const isLastConfigStep = currentConfigStep >= selectedTypes.length - 1;
    setPendingConfigAction(isLastConfigStep ? 'finish' : 'next');
    setSaveSettings(true);
  };

  const handleFinishSetup = async () => {
    // Always move to the next step (workgroup naming) regardless of whether workgroup is created
    setStep(2);
  };
  // Render progress indicator with dynamic steps
  const renderProgressIndicator = () => {
    const steps = [
      { number: 1, label: "Select Integrations" },
      { number: 2, label: "Name Workgroup" },
      ...selectedTypes.map((type, index) => {
        const displayName = getIntegrationDisplayName(type) || type || 'Integration';
        return {
          number: 3 + index,
          label: `Configure ${displayName}`,
        };
      })
    ];

    // Calculate current step number
    const currentStepNumber = step === 3 ? 3 + currentConfigStep : step;

    return (
      <div className="mb-8">
        <div className="relative">
          {/* Scrollable container */}
          <div
            ref={progressContainerRef}
            className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex items-center min-w-max space-x-4 px-[calc(50%-1rem)]">
              {steps.map((stepInfo, index) => (
                <React.Fragment key={stepInfo.number}>
                  {index > 0 && (
                    <div className="flex-none w-8 h-0.5 bg-gray-200 dark:bg-gray-700">
                      <div
                        className={`h-full ${currentStepNumber >= stepInfo.number ? 'bg-blue-600 dark:bg-blue-400' : ''}`}
                        style={{ width: '100%' }}
                      ></div>
                    </div>
                  )}
                  <div
                    ref={el => stepRefs.current[stepInfo.number] = el}
                    className={`flex-none flex items-center ${currentStepNumber >= stepInfo.number ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStepNumber >= stepInfo.number ? 'border-blue-600 dark:border-blue-400' : 'border-gray-300 dark:border-gray-600'
                      }`}>
                      {stepInfo.number}
                    </div>
                    <span className="ml-2 text-sm font-medium whitespace-nowrap">{stepInfo.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Gradient fade indicators for scroll */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
        </div>

        {/* Current step indicator */}
        <div className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
          {steps[currentStepNumber - 1]?.label || 'Getting Started'}
        </div>
      </div>
    );
  };

  // Render configuration form for current system
  const renderCurrentSystemConfig = () => {
    console.log('currentSystem:', currentSystem);
    if (!currentSystem) {
      return (
        <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No integration selected. Choose an integration to configure on the previous step.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 dark:bg-gray-750 p-4 rounded-lg">
        <div className="space-y-4">
      <SettingsPage
        pageType={currentSystem}
        selectedWorkgroupId={createdWorkgroupName}
        loading={loading}
        external_save_button={saveSettings}
        onConfigSaved={handleConfigSaveSuccess}
        onConfigSaveError={handleConfigSaveError}
      />
        </div>
      </div>
    );
  };

// Render step 1 - Workgroup Name
const renderWorkgroupNameStep = () => (
  <div style={stageAnchorStyle}>
    <div className="space-y-6">
      <div>
        <label htmlFor="workgroup_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Workgroup Name
        </label>
        <div className="mt-1">
          <input
            id="workgroup_name"
            name="workgroup_name"
            type="text"
            required
            value={workgroupName}
            onChange={(e) => setWorkgroupName(e.target.value)}
            disabled={!!createdWorkgroupName}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
            placeholder="Enter your workgroup name"
          />
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
          {createdWorkgroupName ? (
            <>
              Your workgroup "{createdWorkgroupName}" has been created.
              <br />
              <br />
              You cannot change this name after it is set.
            </>
          ) : (
            <>
              This will be the name of your team's workgroup.
              <br />
              <br />
              NOTE: YOU CANNOT CHANGE THIS NAME AFTER IT IS SET.
              <br />
              You may delete this workgroup and restart the process to create a new one.
            </>
          )}
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="flex space-x-4">
        <button
          onClick={() => setStep(1)}
          className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back
        </button>
        <button
          onClick={createdWorkgroupName ? () => setStep(3) : handleCreateWorkgroup}
          disabled={loading}
          className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : createdWorkgroupName ? 'Continue to Configure Systems' : 'Next: Configure Systems'}
        </button>
      </div>
    </div>
  </div>
);

// Render step 1 - Select Integrations
const renderSelectIntegrationsStep = () => (
  <div style={stageAnchorStyle}>
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Select Integrations
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Choose the systems you want to connect to your workgroup. You can change this later.
          <br />
          <br />
          We recommend starting with only the few systems you need to get started as more systems will decrease the quality of the responses and take longer for you to setup.
        </p>

        <IntegrationSelectionList
          integrations={availableIntegrations}
          selectedIntegrationIds={selectedTypes}
          onToggle={handleTypeToggle}
          loading={integrationsLoading}
          pendingIntegrationId={pendingIntegrationId}
          selectedBadgeLabel="Selected"
          unselectedBadgeLabel="Not selected"
        />

        {integrationError && (
          <div className="mt-4 rounded-md bg-red-50 dark:bg-red-900/30 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  {integrationError}
                </h3>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      <div>
        <button
          onClick={handleFinishSetup}
          disabled={loading || integrationsLoading}
          className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Setting up...' : integrationsLoading ? 'Loading integrations…' : 'Next: Name Workgroup'}
        </button>
      </div>
    </div>
  </div>
);

// Render step 3 - Configure Systems
const renderConfigureSystemsStep = () => {
  const displayName = getIntegrationDisplayName(currentSystem) || 'Integration';

  return (
    <div style={stageAnchorStyle}>
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Configure {displayName}
          </h3>
          <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-6">
            <p className="text-sm text-blue-700 dark:text-blue-200">
              Don't have your {displayName} credentials handy? You can skip this step for now, but you'll need to configure this integration before you can start chatting with it.
            </p>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Please provide the necessary credentials for your {displayName} integration.
          </p>

          {renderCurrentSystemConfig()}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  {error}
                </h3>
              </div>
            </div>
          </div>
        )}

        <div className="flex space-x-4">
          <button
            onClick={() => {
              if (currentConfigStep > 0) {
                setCurrentConfigStep(currentConfigStep - 1);
              } else {
                setStep(2);
              }
            }}
            className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back
          </button>
          <div className="flex-1 flex space-x-4">
            <button
              onClick={handleNextConfig}
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save & Continue'}
            </button>
            <button
              onClick={async () => {
                // Skip current system configuration
                console.log('Skip button clicked, current step:', currentConfigStep);
                console.log('Total types:', selectedTypes.length);

                if (currentConfigStep < selectedTypes.length - 1) {
                  setCurrentConfigStep(currentConfigStep + 1);
                } else {
                  // All systems configured or skipped, finish setup
                  console.log('Saving workgroup types before skipping');
                  try {
                    const response = await saveWorkgroupTypes(createdWorkgroupName, selectedTypes);
                    if (response.error) {
                      throw new Error(response.error);
                    }
                    console.log('Workgroup types saved successfully');

                    // Create example prompts for the workgroup
                    console.log('Creating example prompts for workgroup:', createdWorkgroupName);
                    await createExamplePrompts(createdWorkgroupName, selectedTypes);
                    console.log('Example prompts created successfully');

                    // Clear onboarding state
                    console.log('Clearing onboarding state');
                    await clearSavedOnboardingState();
                    console.log('Onboarding state cleared');

                    // Add a small delay to ensure Firestore updates are processed
                    await new Promise((resolve) => setTimeout(resolve, 1000));

                    // Force a page reload to ensure clean state
                    window.location.href = '/';
                  } catch (err) {
                    console.error('Error during skip process:', err);
                    setError(err.message || 'Failed to complete setup');
                  }
                }
              }}
              className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Skip for Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Render error message component
const renderErrorMessage = () => (
  error && (
    <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
      <div className="flex">
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
            {error}
          </h3>
        </div>
      </div>
    </div>
  )
);

// Add loading state to the render
if (isLoadingState) {
  return (
    <div
      className={`flex-1 w-full min-h-full bg-white dark:bg-gray-900 flex flex-col ${
        isShortScreen ? 'justify-start' : 'justify-center'
      } px-4 sm:px-6 lg:px-8`}
      style={onboardingContainerStyle}
    >
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Loading your progress...
          </h2>
        </div>
      </div>
    </div>
  );
}

return (
  <div
    className={`flex-1 w-full min-h-full bg-white dark:bg-gray-900 flex flex-col ${
      isShortScreen ? 'justify-start' : 'justify-center'
    } px-4 sm:px-6 lg:px-8`}
    style={onboardingContainerStyle}
  >
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
        Welcome to Interpretos
      </h2>
      <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
        Workgroups are how you manage connections to your systems.
        Let's set up your first workgroup to get started
      </p>
    </div>

    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
        {renderProgressIndicator()}

        {step === 2 && renderWorkgroupNameStep()}
        {step === 1 && renderSelectIntegrationsStep()}
        {step === 3 && renderConfigureSystemsStep()}
      </div>
    </div>
  </div>
);
};

export default OnboardingFlow; 
